/**
 * archetypes.ts — display-side localization for archetype definitions.
 *
 * The 12 archetype definitions live in the database (an immutable seed, see
 * supabase/migrations/20260504_archetype_seed.sql) and are read by id. Their
 * copy is therapeutic content that must reach a Polish user in Polish, so the
 * locale files carry a translation of each field under `archetypes.<id>`.
 *
 * This helper overlays that copy onto a row fetched from the database. Every
 * field falls back to the database text when the locale has no value, so a
 * missing key can never blank a card. The row's `id` is never touched: it is
 * the join key to `user_archetype_assignments` and to the classifier.
 *
 * Why the locale files rather than a Polish column on the table: the app
 * already keeps Polish for arcs, series, preparations and emotions in code
 * next to the English, the i18n parity test guards the keys, and the review
 * packet for Marta is generated from the same files. A schema change on the
 * production database would buy nothing over that.
 */
import type { TFunction } from 'i18next'

/** The four translatable columns; callers may select any subset. */
export interface ArchetypeCopy {
  display_name?: string
  description?: string
  shadow_aspect?: string
  integration_aspect?: string
}

const FIELD_KEYS: Record<keyof ArchetypeCopy, string> = {
  display_name: 'displayName',
  description: 'description',
  shadow_aspect: 'shadowAspect',
  integration_aspect: 'integrationAspect',
}

/**
 * Returns a copy of `def` with each present field replaced by the locale's
 * text for `archetypeId`, or left as-is when the locale has none.
 */
export function localizeArchetype<T extends ArchetypeCopy>(archetypeId: string, def: T, t: TFunction): T {
  const out = { ...def }
  for (const field of Object.keys(FIELD_KEYS) as (keyof ArchetypeCopy)[]) {
    const current = def[field]
    if (typeof current !== 'string') continue
    ;(out as ArchetypeCopy)[field] = t(`archetypes.${archetypeId}.${FIELD_KEYS[field]}`, { defaultValue: current })
  }
  return out
}
